import type { ACPoint } from "../../model/calcAcceleratorOscillator";
import type { AcceleratorOscillatorSettings } from "../../model";
import { COLORS, GRID } from "features/chart";

const TOP = 8;
const BOTTOM = 20;

export function drawAcceleratorOscillator(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  points: ACPoint[],
  visibleStart: number,
  visibleEnd: number,
  settings: AcceleratorOscillatorSettings,
): void {
  const left = GRID.left;
  const right = GRID.right;
  const chartW = width - left - right;
  const chartH = height - TOP - BOTTOM;

  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, width, height);

  const startIdx = Math.floor(visibleStart);
  const endIdx = Math.ceil(visibleEnd);
  const visible = points.filter(
    (p) => p.index >= startIdx && p.index <= endIdx,
  );
  if (!visible.length) return;

  let absMax = 0;
  for (const p of visible) {
    const a = Math.abs(p.value);
    if (a > absMax) absMax = a;
  }
  const pad = (absMax || 1) * 0.1;
  const maxVal = absMax + pad;
  const minVal = -(absMax + pad);
  const range = maxVal - minVal;
  const valToY = (v: number) => TOP + ((maxVal - v) / range) * chartH;
  const zeroY = valToY(0);

  ctx.font = "10px sans-serif";
  const labelW = ctx.measureText(maxVal.toFixed(5)).width;
  const lineEndX = width - right - labelW - 6;

  ctx.strokeStyle = COLORS.grid;
  ctx.lineWidth = 1;
  for (let i = 0; i <= 2; i++) {
    const y = TOP + (chartH * i) / 2;
    ctx.beginPath();
    ctx.moveTo(left, y);
    ctx.lineTo(lineEndX, y);
    ctx.stroke();
  }

  ctx.strokeStyle = "rgba(255,255,255,0.2)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(left, zeroY);
  ctx.lineTo(lineEndX, zeroY);
  ctx.stroke();

  if (settings.mainLineVisible) {
    const visibleCount = Math.floor(visibleEnd) - Math.floor(visibleStart) + 1;
    const cw = chartW / visibleCount;
    const barW = Math.max(1, cw * 0.8);
    ctx.globalAlpha = settings.mainLineOpacity / 100;
    for (const p of visible) {
      const x = left + (p.index - startIdx + 0.5) * cw;
      const y = valToY(p.value);
      const h = Math.abs(y - zeroY) || 1;
      ctx.fillStyle = p.up ? settings.upColor : settings.downColor;
      ctx.fillRect(x - barW / 2, Math.min(y, zeroY), barW, h);
    }
    ctx.globalAlpha = 1;
  }

  ctx.fillStyle = COLORS.label;
  ctx.textAlign = "right";
  ctx.textBaseline = "middle";
  for (let i = 0; i <= 2; i++) {
    const v = maxVal - (range * i) / 2;
    const y = valToY(v);
    if (y < TOP || y > height - BOTTOM) continue;
    ctx.fillText(v === 0 ? "0" : v.toFixed(5), width - right + 2, y);
  }
}
